import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { WifiOff } from 'lucide-react'
import { useAppUpdate } from '../platform/appUpdate'
import { useOnline } from '../platform/network'

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

/** «2 окт, 14:20» - время получения данных, по часам устройства. */
export function formatDataTime(ts: number): string {
  const d = new Date(ts)
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${hh}:${mm}`
}

/**
 * Когда получены данные, которые сейчас на экране: самое раннее время среди
 * открытых (с наблюдателями) запросов нового интерфейса. Шапку с названием
 * клуба не считаем: она меняется редко и тянула бы метку в прошлое.
 */
export function oldestShownDataTime(client: QueryClient): number | null {
  let min: number | null = null
  for (const q of client.getQueryCache().getAll()) {
    if (q.queryKey[0] !== 'redesign' || q.queryKey[1] === 'context') continue
    if (q.getObserversCount() === 0 || q.state.data === undefined || !q.state.dataUpdatedAt) continue
    min = min === null ? q.state.dataUpdatedAt : Math.min(min, q.state.dataUpdatedAt)
  }
  return min
}

function useShownDataTime(enabled: boolean): number | null {
  const client = useQueryClient()
  const subscribe = useCallback(
    (cb: () => void) => (enabled ? client.getQueryCache().subscribe(cb) : () => {}),
    [client, enabled],
  )
  return useSyncExternalStore(
    subscribe,
    () => (enabled ? oldestShownDataTime(client) : null),
    () => null,
  )
}

/**
 * Строка состояния в шапке экрана: «Нет сети. Данные от 2 окт, 14:20» и
 * «Доступна новая версия». Живая область объявляет пропажу и возврат сети
 * экранному диктору. Без сети и без нового релиза ничего не занимает.
 */
export function NetworkStatus() {
  const online = useOnline()
  const stamp = useShownDataTime(!online)
  const update = useAppUpdate()

  // Возврат сети объявляем диктору коротко; видимой плашки не нужно -
  // пропала метка «Нет сети», экраны сами перечитывают данные.
  const [back, setBack] = useState(false)
  const wasOnline = useRef(online)
  useEffect(() => {
    if (online && !wasOnline.current) {
      setBack(true)
      const t = setTimeout(() => setBack(false), 4000)
      wasOnline.current = online
      return () => clearTimeout(t)
    }
    wasOnline.current = online
    setBack(false)
  }, [online])

  return (
    <>
      <span className="pc-net" role="status" aria-live="polite">
        {!online && (
          <span className="pc-net__badge">
            <WifiOff size={14} aria-hidden="true" />
            {stamp ? `Нет сети. Данные от ${formatDataTime(stamp)}` : 'Нет сети'}
          </span>
        )}
        {back && <span className="pc-visually-hidden">Сеть снова есть</span>}
      </span>
      {update && (
        <span className="pc-update">
          <span>Доступна новая версия</span>
          <button type="button" className="pc-btn" onClick={update}>
            Обновить
          </button>
        </span>
      )}
    </>
  )
}
